// src/services/websocketService.ts
import { MESSAGING_CONFIG } from '../config/messaging';

type EventCallback = (data: any) => void;
type ConnectionStatusCallback = (status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'failed') => void;

class WebSocketService {
  private static instance: WebSocketService;
  private websocket: WebSocket | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private connectionStatusListeners: Set<ConnectionStatusCallback> = new Set();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private isConnecting: boolean = false;
  private pingInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private currentUserId: number | null = null;

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Get the current connection status
   */
  isConnected(): boolean {
    return this.websocket?.readyState === WebSocket.OPEN;
  }

  /**
   * Connect to WebSocket server
   */
  async connect(userId: number): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('[WebSocket] Connection already in progress, skipping');
      return;
    }

    // Already connected for this user
    if (this.websocket?.readyState === WebSocket.OPEN && this.currentUserId === userId) {
      console.log('[WebSocket] Already connected for user', userId);
      return;
    }

    // WebSocket is connecting (not yet open)
    if (this.websocket?.readyState === WebSocket.CONNECTING) {
      console.log('[WebSocket] WebSocket is still connecting, skipping');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('[WebSocket] No auth token available');
      this.notifyConnectionStatus('error');
      return;
    }

    // Close existing connection if any (only if different user or closed state)
    if (this.websocket && this.currentUserId !== userId) {
      console.log('[WebSocket] Different user, closing existing connection');
      this.closeExistingConnection();
    }

    this.currentUserId = userId;
    this.isConnecting = true;
    this.notifyConnectionStatus('connecting');

    const wsUrl = `${MESSAGING_CONFIG.WS_URL}?token=${token}`;
    console.log('[WebSocket] Connecting to:', wsUrl);

    try {
      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log('[WebSocket] Connected successfully!');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyConnectionStatus('connected');
        this.startPingInterval();

        // Send initial ping to verify connection
        setTimeout(() => {
          this.sendPing();
        }, 1000);
      };

      this.websocket.onmessage = (event: MessageEvent) => {
        this.handleMessage(event);
      };

      this.websocket.onclose = (event: CloseEvent) => {
        console.log('[WebSocket] Disconnected, code:', event.code, 'reason:', event.reason);
        this.isConnecting = false;
        this.stopPingInterval();
        this.notifyConnectionStatus('disconnected');

        // Only reconnect for non-intentional closures
        // 1000 = normal close, 1001 = going away (navigation)
        if (event.code !== 1000 && event.code !== 1001 && this.currentUserId) {
          this.attemptReconnect();
        }
      };

      this.websocket.onerror = (error: Event) => {
        console.error('[WebSocket] Connection error:', error);
        console.error('[WebSocket] ReadyState:', this.websocket?.readyState);
        this.isConnecting = false;
        this.notifyConnectionStatus('error');
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed with exception:', error);
      this.isConnecting = false;
      this.notifyConnectionStatus('error');
    }
  }

  /**
   * Close existing WebSocket connection
   */
  private closeExistingConnection(): void {
    if (this.websocket) {
      try {
        this.websocket.close(1000, 'Reconnecting');
      } catch (e) {
        // Ignore close errors
      }
      this.websocket = null;
    }
    this.stopPingInterval();
    this.clearReconnectTimeout();
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WebSocket] Max reconnect attempts reached');
      this.notifyConnectionStatus('failed');
      return;
    }

    this.reconnectAttempts++;

    // Exponential backoff with jitter
    const baseDelay = MESSAGING_CONFIG.WS_RECONNECT_INITIAL_DELAY;
    const maxDelay = MESSAGING_CONFIG.WS_RECONNECT_MAX_DELAY;
    const multiplier = MESSAGING_CONFIG.WS_RECONNECT_MULTIPLIER;

    const delay = Math.min(
      baseDelay * Math.pow(multiplier, this.reconnectAttempts - 1),
      maxDelay
    );

    // Add jitter (±20%)
    const jitter = delay * 0.2 * (Math.random() * 2 - 1);
    const finalDelay = delay + jitter;

    console.log(`[WebSocket] Reconnecting in ${Math.round(finalDelay / 1000)}s (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.currentUserId && (!this.websocket || this.websocket.readyState !== WebSocket.OPEN)) {
        this.connect(this.currentUserId);
      }
    }, finalDelay);
  }

  /**
   * Clear reconnect timeout
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, MESSAGING_CONFIG.WS_PING_INTERVAL);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Send ping to server
   */
  private sendPing(): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      // Backend expects 'event' field, not 'type'
      this.websocket.send(JSON.stringify({ event: 'ping' }));
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.log('[WebSocket] Disconnecting - called from:', new Error().stack);
    this.currentUserId = null;
    this.closeExistingConnection();
    this.clearReconnectTimeout();
    this.reconnectAttempts = 0;
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      // Backend uses 'event' field, get the event type
      const eventType = data.event || data.type;

      // Handle heartbeat/pong silently
      if (eventType === 'pong' || eventType === 'heartbeat') {
        return;
      }

      console.log('[WebSocket] Received event:', eventType);
      console.log('[WebSocket] Event data:', JSON.stringify(data, null, 2));

      // Notify listeners based on event type
      // Extract data payload if present (backend sends { event: '...', data: {...} })
      const eventData = data.data || data;
      // Add event_type separately to not overwrite message 'type' field
      this.notifyListeners(eventType, { ...eventData, event_type: eventType });
    } catch (error) {
      console.error('[WebSocket] Error parsing message:', error);
    }
  }

  /**
   * Send typing indicator
   */
  sendTyping(conversationId?: number, groupId?: number, isTyping: boolean = true): void {
    if (this.websocket?.readyState !== WebSocket.OPEN) {
      return;
    }

    // Backend expects { event: 'typing', data: { ... } }
    const data: any = { is_typing: isTyping };
    if (conversationId) {
      data.conversation_id = conversationId;
    }
    if (groupId) {
      data.group_id = groupId;
    }

    this.websocket.send(JSON.stringify({ event: 'typing', data }));
  }

  /**
   * Subscribe to a specific event type
   */
  on(eventType: string, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.off(eventType, callback);
    };
  }

  /**
   * Unsubscribe from a specific event type
   */
  off(eventType: string, callback: EventCallback): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  /**
   * Subscribe to connection status changes
   */
  onConnectionStatusChange(callback: ConnectionStatusCallback): () => void {
    this.connectionStatusListeners.add(callback);
    return () => {
      this.connectionStatusListeners.delete(callback);
    };
  }

  /**
   * Notify listeners of an event
   */
  private notifyListeners(eventType: string, data: any): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('[WebSocket] Error in event listener:', error);
        }
      });
    }

    // Also notify 'all' listeners
    const allListeners = this.listeners.get('all');
    if (allListeners) {
      allListeners.forEach((callback) => {
        try {
          callback({ type: eventType, ...data });
        } catch (error) {
          console.error('[WebSocket] Error in "all" listener:', error);
        }
      });
    }
  }

  /**
   * Notify connection status listeners
   */
  private notifyConnectionStatus(status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'failed'): void {
    this.connectionStatusListeners.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        console.error('[WebSocket] Error in connection status listener:', error);
      }
    });
  }
}

// Export singleton instance
const websocketService = WebSocketService.getInstance();
export default websocketService;
